import { useEffect, useState } from "react";
import { Link, useLocation } from "@tanstack/react-router";
import { useFeatureFlag } from "@/lib/feature-flags";
import { useLanguage } from "@/hooks/useLanguage";
import { trackEvent } from "@/lib/audit";
import { PREVIEW_MODE } from "@/lib/previewMode";

const STORAGE_KEY = "ss2026_registered";
const GUEST_KEY = "ss2026_guest_mode";

function isRegistered() {
  if (typeof window === "undefined") return true;
  try { return window.localStorage.getItem(STORAGE_KEY) === "true"; } catch { return false; }
}
function isGuest() {
  if (typeof window === "undefined") return true;
  try { return window.localStorage.getItem(GUEST_KEY) === "true"; } catch { return false; }
}

/**
 * Registration gate. Renders nothing unless the flag
 * `require_registration_before_access` is enabled and the user has not
 * registered (or chosen guest mode if `allow_guest_mode` is enabled).
 * Excludes /welcome, /registro and admin routes from gating.
 */
export function RegistrationGate() {
  const requireRegistration = useFeatureFlag("require_registration_before_access", false);
  const allowGuest = useFeatureFlag("allow_guest_mode", true);
  const { t } = useLanguage();
  const { pathname } = useLocation();
  const [registered, setRegistered] = useState(true);
  const [guest, setGuest] = useState(true);

  useEffect(() => {
    setRegistered(isRegistered());
    setGuest(isGuest());
    const onReg = () => setRegistered(true);
    window.addEventListener("ss2026:registered", onReg);
    return () => window.removeEventListener("ss2026:registered", onReg);
  }, []);

  const exempt =
    pathname === "/" ||
    pathname.startsWith("/nation-wars") ||
    pathname.startsWith("/quinielas") ||
    pathname.startsWith("/showcase") ||
    pathname.startsWith("/media-blitz") ||
    pathname.startsWith("/ambassadors") ||
    pathname.startsWith("/social") ||
    pathname.startsWith("/ayuda") ||
    pathname.startsWith("/login") ||
    pathname.startsWith("/auth") ||
    pathname.startsWith("/wc") ||
    pathname.startsWith("/s/") ||
    pathname.startsWith("/r/") ||
    pathname.startsWith("/welcome") ||
    pathname.startsWith("/registro") ||
    pathname.startsWith("/admin") ||
    pathname.startsWith("/tenant-portal") ||
    pathname.startsWith("/validar-voucher") ||
    pathname.startsWith("/api");

  const blocked =
    !PREVIEW_MODE &&
    requireRegistration && !exempt && !registered && !(allowGuest && guest);

  useEffect(() => {
    if (blocked) void trackEvent("registration_gate_shown", { pathname });
  }, [blocked, pathname]);

  if (!blocked) return null;

  const onGuest = () => {
    try { window.localStorage.setItem(GUEST_KEY, "true"); } catch { /* noop */ }
    setGuest(true);
    void trackEvent("registration_gate_bypassed", { mode: "guest" });
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-background/95 px-4 backdrop-blur-md">
      <div className="w-full max-w-md rounded-3xl border border-white/10 bg-gradient-to-b from-[oklch(0.18_0.02_270)]/95 to-[oklch(0.08_0.02_270)]/95 p-6 text-center shadow-2xl">
        <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-primary">
          {t("gate_eyebrow")}
        </p>
        <h2 className="mt-2 text-2xl font-black text-foreground">{t("gate_title")}</h2>
        <p className="mt-2 text-sm text-muted-foreground">{t("gate_sub")}</p>

        <div className="mt-5 flex flex-col gap-2">
          <Link
            to="/registro"
            onClick={() => void trackEvent("registration_gate_completed", { action: "register" })}
            className="rounded-xl bg-primary px-5 py-3 text-sm font-bold text-primary-foreground shadow"
          >
            {t("gate_cta_register")}
          </Link>
          <Link
            to="/welcome"
            search={{ src: undefined, ref: undefined, tenant: undefined, lang: undefined, from: undefined }}
            className="rounded-xl border border-border bg-background px-5 py-3 text-sm font-bold text-foreground"
          >
            {t("gate_cta_learn")}
          </Link>
          {allowGuest && (
            <button
              onClick={onGuest}
              className="rounded-xl bg-accent/40 px-5 py-3 text-sm font-bold text-foreground"
            >
              {t("gate_cta_guest")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}